import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Plus, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, Calendar, Sun } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Convert HH:MM (24h) to a display label "1:00 PM".
const fmtTime = (t) => {
  if (!t || !/^\d{2}:\d{2}$/.test(t)) return t || '';
  const [hh, mm] = t.split(':').map(Number);
  const suffix = hh < 12 ? 'AM' : 'PM';
  const h12 = hh % 12 || 12;
  return `${h12}:${String(mm).padStart(2, '0')} ${suffix}`;
};

const SECTIONS = [
  { key: 'weekday', label: 'Weekdays (Mon – Fri)', icon: Calendar, accent: '#FF3B6B' },
  { key: 'weekend', label: 'Weekends (Sat & Sun)', icon: Sun, accent: '#FFC107' },
];

const SectionCard = ({ section, slots, onAdd, onToggle, onDelete, onMove }) => {
  const [newTime, setNewTime] = useState('');
  const Icon = section.icon;
  const sectionSlots = slots.filter((s) => s.day_type === section.key);

  const handleAdd = async () => {
    if (!newTime || !/^\d{2}:\d{2}$/.test(newTime)) {
      toast.error('Please pick a valid time');
      return;
    }
    await onAdd(section.key, newTime);
    setNewTime('');
  };

  return (
    <div className="rounded-2xl border-2 border-[#E8E5DF] p-5 bg-white" data-testid={`time-slots-section-${section.key}`}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5" style={{ color: section.accent }} />
          <h3 className="font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>{section.label}</h3>
        </div>
        <span className="text-xs text-[#6B6B7A]">{sectionSlots.filter((s) => s.active).length} active / {sectionSlots.length} total</span>
      </div>

      <div className="flex items-end gap-2 mb-4 p-3 rounded-xl bg-[#FAF8F4]">
        <div className="flex-1">
          <label className="text-[11px] text-[#6B6B7A]">Add a new slot</label>
          <Input
            type="time"
            value={newTime}
            onChange={(e) => setNewTime(e.target.value)}
            data-testid={`add-slot-time-${section.key}`}
          />
        </div>
        <Button
          onClick={handleAdd}
          className="rounded-full bg-[#FF3B6B] hover:bg-[#FF3B6B]/90 text-white"
          data-testid={`add-slot-btn-${section.key}`}
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Slot
        </Button>
      </div>

      <div className="space-y-2">
        <AnimatePresence>
          {sectionSlots.length === 0 && (
            <p className="text-sm text-[#6B6B7A] italic text-center py-4">No slots yet — add one above.</p>
          )}
          {sectionSlots.map((s, idx) => (
            <motion.div
              key={s.id}
              layout
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -10 }}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${
                s.active ? 'border-[#E8E5DF] bg-white' : 'border-dashed border-[#D5D2CC] bg-[#F4F1EC]/50 opacity-70'
              }`}
              data-testid={`slot-row-${s.id}`}
            >
              <div className="flex flex-col">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5"
                  disabled={idx === 0}
                  onClick={() => onMove(section.key, idx, idx - 1)}
                  data-testid={`slot-up-${s.id}`}
                >
                  <ArrowUp className="w-3 h-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5"
                  disabled={idx === sectionSlots.length - 1}
                  onClick={() => onMove(section.key, idx, idx + 1)}
                  data-testid={`slot-down-${s.id}`}
                >
                  <ArrowDown className="w-3 h-3" />
                </Button>
              </div>
              <div className="flex-1">
                <p className="font-mono text-sm">{s.time} <span className="text-[#6B6B7A] font-sans">· {fmtTime(s.time)}</span></p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onToggle(s)}
                data-testid={`slot-toggle-${s.id}`}
                title={s.active ? 'Hide from booking' : 'Show in booking'}
              >
                {s.active ? <Eye className="w-4 h-4 text-[#FF3B6B]" /> : <EyeOff className="w-4 h-4 text-[#6B6B7A]" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onDelete(s)}
                className="text-red-600 hover:text-red-700"
                data-testid={`slot-delete-${s.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export const CrmTimeSlots = () => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/time-slots`, { withCredentials: true });
      setSlots(r.data || []);
    } catch {
      toast.error('Failed to load time slots');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sortedSlots = useMemo(
    () => [...slots].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.time.localeCompare(b.time)),
    [slots],
  );

  const onAdd = async (day_type, time) => {
    try {
      await axios.post(
        `${API_URL}/api/admin/time-slots`,
        { day_type, time },
        { withCredentials: true },
      );
      toast.success(`Slot ${fmtTime(time)} added to ${day_type}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add slot');
    }
  };

  const onToggle = async (slot) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/time-slots/${slot.id}`,
        { active: !slot.active },
        { withCredentials: true },
      );
      toast.success(slot.active ? 'Slot hidden' : 'Slot enabled');
      await load();
    } catch {
      toast.error('Failed to toggle slot');
    }
  };

  const onDelete = async (slot) => {
    if (!window.confirm(`Remove ${fmtTime(slot.time)} from ${slot.day_type}? Existing bookings remain valid.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/time-slots/${slot.id}`, { withCredentials: true });
      toast.success('Slot removed');
      await load();
    } catch {
      toast.error('Failed to remove slot');
    }
  };

  const onMove = async (day_type, fromIdx, toIdx) => {
    const sectionSlots = sortedSlots.filter((s) => s.day_type === day_type);
    const reordered = [...sectionSlots];
    const [removed] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, removed);
    try {
      await axios.post(
        `${API_URL}/api/admin/time-slots/reorder`,
        { day_type, ordered_ids: reordered.map((s) => s.id) },
        { withCredentials: true },
      );
      await load();
    } catch {
      toast.error('Failed to reorder');
    }
  };

  return (
    <div className="space-y-6" data-testid="crm-time-slots-page">
      <div>
        <h1 className="text-2xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>Time Slots</h1>
        <p className="text-sm text-[#6B6B7A] mt-1">
          Control which times customers can book. Changes affect the availability calendar and the "Pick a time" step in real time.
        </p>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#6B6B7A]">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {SECTIONS.map((section) => (
            <SectionCard
              key={section.key}
              section={section}
              slots={sortedSlots}
              onAdd={onAdd}
              onToggle={onToggle}
              onDelete={onDelete}
              onMove={onMove}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CrmTimeSlots;
